import { Component, Input, OnInit, OnChanges, SimpleChanges, signal, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { NationChartComponent } from '../../../../../shared/components/charts/nation-chart/nation-chart.component';
import { ScrollTableComponent, TableHeader } from '../../../../../shared/components/tables/scrolled-table/scroll-table.component';
import { SelectionComponent, SelectionOption } from '../../../../../shared/components/ui/selection/selection.component';
import { DateRangePickerOutput } from '../../../../../shared/components/date-picker/date-picker.component';
import { ApiService } from '../../../../../core/services/api.service';
import dayjs from 'dayjs';
import quarterOfYear from 'dayjs/plugin/quarterOfYear';
import { forkJoin } from 'rxjs';
import { IndividualToasterComponent } from "../../../../../shared/components/loader/loader.component";

dayjs.extend(quarterOfYear);

interface StateData {
  country_id: number;
  country_name: string;
  country_code: string;
  state_name: string;
  total_doctors: number;
  dental: number;
  healthcare: number;
  non_healthcare: number;
}

type DataType = 'total' | 'dental' | 'healthcare' | 'non_healthcare';

@Component({
  selector: 'app-state-contracts',
  imports: [
    CommonModule,
    FormsModule,
    NationChartComponent,
    ScrollTableComponent,
    SelectionComponent,
    IndividualToasterComponent
],
  templateUrl: './state-contracts.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styles: ``
})
export class StateContractsComponent implements OnInit, OnChanges {
  @Input() dateRange!: DateRangePickerOutput;
  @Input() productServerName: string = 'denefits'; // Dynamic product server name
  @Input() requiresAccessToken: boolean = false; // Whether the product requires an access token
  @Input() cocu_id: number = 1;
  @Input() country_id: number = 1;
  selectedDataType: DataType = 'total';
  isLoading = signal(false)
  // Selection options
  dataTypeOptions: any[] = [
    { value: 'total', label: 'Total Count', industryType: '0' },
    { value: 'dental', label: 'Dental', industryType: '1' },
    { value: 'healthcare', label: 'Healthcare', industryType: '2' },
    { value: 'non_healthcare', label: 'Non-Healthcare', industryType: '3' }
  ];

  // Map data
  stateChartData: any[] = [];
  valueKeys = {
    keyName: 'state',
    valueKey: 'count'
  };

  // Table data
  tableData: any[] = [];
  previousTableData: any[] = [];
  tableHeaders: TableHeader[] = [
    { label: 'Sr', key: 'sr', align: 'text-left' },
    { label: 'State', key: 'state', align: 'text-left' },
    { label: 'Current', key: 'current', align: 'text-center' },
    { label: 'Previous', key: 'previous', align: 'text-center' }
  ];

  // Stats summary
  currentTotal: number = 0;
  previousTotal: number = 0;
  percentChange: number = 0;

  constructor(private apiService: ApiService) { }

  get selectedValue(): DataType {
    return this.selectedDataType;
  }

  ngOnInit(): void {
  }

  ngOnChanges(changes: SimpleChanges): void {
    if ((changes['dateRange'] && changes['dateRange'].currentValue) || 
        (changes['productServerName'] && !changes['productServerName'].firstChange) ||
        changes['cocu_id'] || changes['country_id']) {
      // Update table headers with date range labels
      this.updateTableHeaders();
      
      // When dateRange, product or currency changes, reload data
      this.fetchAllData();
    }
  }

  private updateTableHeaders(): void {
    if (!this.dateRange) return;

    const unit = this.dateRange.unit || 'day';
    
    // Format date range label based on unit
    const currentLabel = this.dateRange.start_date && this.dateRange.end_date
      ? `Current (${this.formatDateRangeByUnit(this.dateRange.start_date, this.dateRange.end_date, unit)})`
      : 'Current';

    const hasPreviousPeriod = this.dateRange.previous_start_date && this.dateRange.previous_end_date;

    // Base headers always include Sr, State, and Current
    const baseHeaders: TableHeader[] = [
      { label: 'Sr', key: 'sr', align: 'text-left' },
      { label: 'State', key: 'state', align: 'text-left' },
      { label: currentLabel, key: 'current', align: 'text-left', showProgress: true }
    ];

    // Only add Previous column if previous period exists
    if (hasPreviousPeriod) {
      const previousLabel = `Previous (${this.formatDateRangeByUnit(this.dateRange.previous_start_date!, this.dateRange.previous_end_date!, unit)})`;
      baseHeaders.push({ label: previousLabel, key: 'previous', align: 'text-left' });
    }

    this.tableHeaders = baseHeaders;
  }

  private formatDateRangeByUnit(startDate: string, endDate: string, unit: string): string {
    const start = dayjs(startDate);
    const end = dayjs(endDate);

    switch (unit) {
      case 'hour':
        // For hourly: show date with hour range
        if (start.isSame(end, 'day')) {
          return `${start.format('DD MMM')} (${start.format('HH:mm')} - ${end.format('HH:mm')})`;
        }
        return `${start.format('DD MMM HH:mm')} - ${end.format('DD MMM HH:mm')}`;
      
      case 'day':
        // For daily: show date range
        if (start.isSame(end, 'month')) {
          return `${start.format('DD')} - ${end.format('DD MMM YYYY')}`;
        }
        return `${start.format('DD MMM')} - ${end.format('DD MMM YYYY')}`;
      
      case 'week':
        // For weekly: show date range
        return `${start.format('DD MMM')} - ${end.format('DD MMM YYYY')}`;
      
      case 'month':
        // For monthly: show month range
        if (start.isSame(end, 'year')) {
          return `${start.format('MMM')} - ${end.format('MMM YYYY')}`;
        }
        return `${start.format('MMM YYYY')} - ${end.format('MMM YYYY')}`;
      
      case 'quarter':
        // For quarterly: show quarter range
        return `Q${start.quarter()} ${start.format('YYYY')} - Q${end.quarter()} ${end.format('YYYY')}`;
      
      case 'year':
        // For yearly: show year range
        if (start.year() === end.year()) {
          return start.format('YYYY');
        }
        return `${start.format('YYYY')} - ${end.format('YYYY')}`;
      
      default:
        return `${start.format('MMM YYYY')} - ${end.format('MMM YYYY')}`;
    }
  }

  onDataTypeChange(value: DataType | DataType[]): void {
    const singleValue = Array.isArray(value) ? value[0] : value;
    if (singleValue) {
      this.selectedDataType = singleValue;
      
      // Reload data when data type changes
      if (this.dateRange) {
        this.fetchAllData();
      }
    }
  }

  private fetchAllData() {
    if (!this.dateRange) {
      console.warn('DateRange not available yet');
      return;
    }
this.isLoading.set(true)
    const hasPreviousData = this.dateRange.previous_start_date && this.dateRange.previous_end_date;

    this.tableData = [];
    this.stateChartData = [];
    this.previousTableData = [];

    // Make current period API call
    const currentCall$ = this.makeApiCall(false);
    
    if (hasPreviousData) {
      // Make both calls and wait for both to complete
      const previousCall$ = this.makeApiCall(true);
      
      forkJoin({
        current: currentCall$,
        previous: previousCall$
      }).subscribe({
        next: (results) => {
          // Process current data first
          this.processCurrentData(results.current);
          
          // Then process previous data
          this.processPreviousData(results.previous);
          this.isLoading.set(false)
        },
        error: (error) => {
          console.error('Error fetching data:', error);
          this.isLoading.set(false)
        }
      });
    } else {
      // Only fetch current data
      currentCall$.subscribe({
        next: (response) => {
          this.processCurrentData(response);
          this.previousTableData = [];
          this.isLoading.set(false)
        },
        error: (error) => {
          console.error('Error fetching current data:', error);
          this.isLoading.set(false)
        }
      });
    }
  }

  private makeApiCall(usePrevious: boolean) {
    const selectedOption = this.dataTypeOptions.find(opt => opt.value === this.selectedDataType);
    const industryType = selectedOption?.industryType || '0';

    const startTime = usePrevious ? this.dateRange.previous_start_date : this.dateRange.start_date;
    const endTime = usePrevious ? this.dateRange.previous_end_date : this.dateRange.end_date;

    const payload: any = {
      start_time: dayjs(startTime || new Date()).format('YYYY-MM-DD'),
      end_time: dayjs(endTime || new Date()).format('YYYY-MM-DD'),
      is_dummy: 0,
      cocu_id: this.cocu_id,
      country_id: this.country_id,
      // timezone: 'PST',
      unit: this.dateRange?.unit || 'day',
      outputFormat: 'db',
      industryType: industryType,
    };

    if (this.productServerName === 'credee') {
      payload['timezone'] = 'PDT';
    }

    return this.apiService.post(`/visionboard/stats/country_state_contracts`, payload, {
      addAccessToken: this.requiresAccessToken,
      productServerName: this.productServerName,
      showErrorToaster: true,
      showSuccessToaster: false,
      showLoader: false, // Only show loader for current call
      // showLoader: !usePrevious, // Only show loader for current call
    });
  }

  private processCurrentData(response: any) {
    if (response?.is_error === 0) {
      const stateRecords = response?.countries?.[0]?.statesData || [];
      
      // Process current period data
      this.tableData = stateRecords
        .map((state: any, index: number) => {
          const currentValue = this.getApiValueByType(state, this.selectedDataType);
          
          return {
            sr: index + 1,
            state: state.state_name || state.state,
            current: currentValue,
            previous: 0, // Will be filled when previous data is processed
          };
        })
        .sort((a: any, b: any) => b.current - a.current);

      // Transform data for map
      this.stateChartData = stateRecords.map((state: any) => ({
        state: state.state_name || state.state,
        count: state.count || 0,
        dental: 0,
        healthcare: 0,
        non_healthcare: 0,
        total: state.count || 0
      }));

      // Calculate current total
      this.currentTotal = this.tableData.reduce((sum: number, row: any) => sum + row.current, 0);
    }
  }

  private processPreviousData(response: any) {
    if (response?.is_error === 0) {
      const stateRecords = response?.countries?.[0]?.statesData || [];
      const previousDataMap = new Map<string, number>();
      
      stateRecords.forEach((state: any) => {
        const stateName = state.state_name || state.state;
        const value = this.getApiValueByType(state, this.selectedDataType);
        previousDataMap.set(stateName, value);
      });

      // Create a set of current state names for quick lookup
      const currentStatesSet = new Set(this.tableData.map((row: any) => row.state));

      // Update tableData with previous values
      this.tableData = this.tableData.map((row: any) => {
        const previousValue = previousDataMap.get(row.state) || 0;
        return {
          ...row,
          previous: previousValue
        };
      });

      // Add states that exist in previous period but not in current period
      previousDataMap.forEach((value, stateName) => {
        if (!currentStatesSet.has(stateName)) {
          this.tableData.push({
            sr: this.tableData.length + 1,
            state: stateName,
            current: 0,
            previous: value
          });
        }
      });

      // Re-sort the table data by current value (descending), then by previous value
      this.tableData = this.tableData.sort((a: any, b: any) => {
        if (b.current !== a.current) {
          return b.current - a.current;
        }
        return b.previous - a.previous;
      });

      // Update serial numbers after sorting
      this.tableData = this.tableData.map((row: any, index: number) => ({
        ...row,
        sr: index + 1
      }));

      // Create previousTableData for progress comparison
      this.previousTableData = this.tableData.map((row: any) => ({
        sr: row.sr,
        state: row.state,
        current: row.previous,
        previous: 0
      }));

      // Calculate previous total and percent change
      this.previousTotal = this.tableData.reduce((sum: number, row: any) => sum + row.previous, 0);
      this.percentChange = this.previousTotal > 0 
        ? ((this.currentTotal - this.previousTotal) / this.previousTotal) * 100 
        : 0;
    }
  }



  private getApiValueByType(state: any, type: DataType): number {
    // The API returns a 'count' field that represents the data based on industryType sent in the request
    // So we always use 'count' regardless of the type
    return state.count || 0;
  }

  private getValueByType(state: StateData, type: DataType): number {
    switch (type) {
      case 'total':
        return state.total_doctors;
      case 'dental':
        return state.dental;
      case 'healthcare':
        return state.healthcare;
      case 'non_healthcare':
        return state.non_healthcare;
      default:
        return state.total_doctors;
    }
  }
}
